import { Button, Table } from 'antd';
import { useMemo, useRef } from 'react';
import useMultiBreakdownTable from './hooks/useMultiBreakdownTable';
import { calculateContainerHeight } from 'utils/helperMethods';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { startCase } from 'lodash';
import { useProfitPoolOverviewContext } from './context/ProfitPoolOverviewContext';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';

const BreakDownTable = () => {
    const tableHeight = calculateContainerHeight(2);
    const currentRef = useRef<any>(null);
    const { data, isPending, tableDef, isError, isSuccess, fetchNextPage } = useMultiBreakdownTable();
    const { selectedSubFilter } = useProfitPoolOverviewContext();
    // Convert table columns and data to Excel format
    const excelExportData = useMemo(() => {
        if (!data?.length || !tableDef?.length) {
            return { headers: [], data: [] };
        }

        // Flatten nested columns to create headers
        const headers: string[] = [];
        const dataIndexes: string[] = [];

        tableDef.forEach((col: any) => {
            if (col.children && Array.isArray(col.children)) {
                // Handle nested columns (e.g., Current Year, Year Ago, Difference)
                col.children.forEach((child: any) => {
                    headers.push(`${col.title} - ${child.title}`);
                    dataIndexes.push(child.dataIndex);
                });
            } else {
                // Handle regular columns
                headers.push(col.title || col.dataIndex);
                dataIndexes.push(col.dataIndex);
            }
        });

        // Transform data rows
        const excelData = data.map((row: any) => {
            return dataIndexes.map((dataIndex: string) => {
                // Handle Difference columns - calculate percentage from numeric values
                if (dataIndex.endsWith('_diff')) {
                    const kpiName = dataIndex.replace('_diff', '');
                    const cyVal = row[`${kpiName}_cy_val`];
                    const yaVal = row[`${kpiName}_ya_val`];

                    if (cyVal != null && yaVal != null && yaVal !== 0) {
                        const diff = cyVal - yaVal;
                        const diffPercent = ((diff / yaVal) * 100).toFixed(1);
                        return isNaN(Number(diffPercent)) ? '0.0%' : `${diffPercent}%`;
                    }
                    return '0.0%';
                }

                // Get the value from the row (formatted strings like "1.2M")
                const value = row[dataIndex];
                return value || '';
            });
        });

        return { headers, data: excelData };
    }, [data, tableDef]);

    return (
        <div>
            <div className="flex flex-row justify-between py-2 mb-4">
                <p className="text-lg font-semibold">{'Breakdown'}</p>
                <div className="flex flex-row gap-2">
                    <div className="flex flex-row gap-2">
                        <CopyToClipBoard refOfComponent={currentRef} />
                    </div>
                    {excelExportData.headers.length > 0 && (
                        <DownloadExcel
                            title={`Breakdown by ${startCase(selectedSubFilter?.dataKey)}`}
                            excelData={excelExportData.data}
                            headers={excelExportData.headers}
                        />
                    )}
                </div>
            </div>
            <div ref={currentRef}>
                <Table
                    columns={tableDef}
                    dataSource={data}
                    pagination={false}
                    sticky={true}
                    bordered
                    loading={isPending}
                    scroll={{ x: window.innerWidth - 300, y: tableHeight }}
                />
            </div>
            <div style={{ textAlign: 'center' }}>
                <Button
                    type="primary"
                    color="purple"
                    className="m-2"
                    onClick={() => fetchNextPage()}
                    disabled={isPending}
                >
                    {isPending ? 'Loading...' : 'Load More'}
                </Button>
            </div>
        </div>
    );
};

export default BreakDownTable;
